import React, { useState, useRef } from 'react';
import { axiosWithRetry } from '../utils/withRetry';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { Upload, X } from 'lucide-react';
import { API } from '../constants';

export default function FileUpload({
  onFileUploaded,
  onFileSelected,
  isUploading,
  uploadProgress,
  uploadElapsed = 0,
  uploadFileSize = 0,
  onUploadStart,
  onUploadProgress,
  onUploadComplete,
  cancelRef,           // optional ref – caller can call cancelRef.current() to abort
}) {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const abortControllerRef = useRef(null);

  // Cancel any in-flight upload
  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  // Expose cancel function to parent via cancelRef
  if (cancelRef) {
    cancelRef.current = cancelUpload;
  }

  const handleFileSelect = async (file) => {
    if (!file.name.toLowerCase().endsWith('.stl')) {
      toast.error('Bitte wählen Sie eine STL-Datei aus');
      return;
    }

    // Client-side size guard: reject before upload (server limit = 200 MB)
    const MAX_STL_BYTES = 200 * 1024 * 1024;
    if (file.size > MAX_STL_BYTES) {
      toast.error(`Datei zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximale Dateigröße: 200 MB.`);
      return;
    }
    if (file.size < 84) {
      toast.error('Datei zu klein oder beschädigt – keine gültige STL-Datei.');
      return;
    }

    // Abort any previous upload that is still running
    cancelUpload();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (process.env.NODE_ENV !== "production") console.log("Starting file upload for:", file.name);

    if (onFileSelected) {
      onFileSelected(file);
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      onUploadStart?.();
      if (process.env.NODE_ENV !== "production") console.log("Uploading file to backend...");
      const response = await axiosWithRetry({
        method: 'post',
        url: `${API}/upload-stl`,
        data: formData,
        signal: controller.signal,
        onUploadProgress: (event) => {
          const total = event.total || file.size;
          if (!total) return;
          const percent = Math.min(99, Math.round((event.loaded / total) * 100));
          onUploadProgress?.(percent);
        }
      });

      abortControllerRef.current = null;
      if (process.env.NODE_ENV !== "production") console.log("File upload successful:", response.data);
      onUploadProgress?.(100);
      onUploadComplete?.();
      onFileUploaded(response.data, file);

      const validation = response.data.validation;
      if (!validation.is_valid) {
        toast.error(`STL-Datei hat Fehler: ${validation.errors.join(', ')}`);
      } else if (validation.warnings?.length > 0) {
        toast.warning(`STL analysiert mit Hinweisen: ${validation.warnings.join(', ')}`);
      } else {
        toast.success(`STL-Datei erfolgreich analysiert und ist druckbar!`);
      }
    } catch (error) {
      abortControllerRef.current = null;
      // Aborted by user – reset progress silently, no error toast
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        if (process.env.NODE_ENV !== 'production') console.log('Upload abgebrochen.');
        onUploadProgress?.(0);
        onUploadComplete?.();
        return;
      }
      // Rate limit exceeded (UX3)
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers?.['retry-after'];
        const secs = retryAfter ? ` Bitte warte ${retryAfter} Sekunden.` : '';
        toast.error(`Zu viele Uploads.${secs}`);
        onUploadProgress?.(0);
        onUploadComplete?.();
        return;
      }
      if (process.env.NODE_ENV !== 'production') console.error('Upload error:', error);
      onUploadProgress?.(0);
      onUploadComplete?.();
      if (error.response?.data?.detail) {
        toast.error(`Fehler: ${error.response.data.detail}`);
      } else {
        toast.error('Fehler beim Hochladen der Datei');
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.name.toLowerCase().endsWith('.stl'));
    if (files.length === 0) return;
    files.reduce((p, file) => p.then(() => handleFileSelect(file)), Promise.resolve());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <Card className="border-2 border-dashed border-gray-300 hover:border-red-400 transition-colors">
      <CardContent className="flex flex-col items-center justify-center p-4 sm:p-8">
        <div
          className={`w-full h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
            isDragOver ? 'border-blue-500 bg-red-50' : 'border-gray-300 hover:border-red-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-8 h-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-600 text-center">
            STL-Dateien hierher ziehen oder klicken zum Auswählen
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Automatische Druckbarkeits-Prüfung inklusive
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          name="stl_file"
          accept=".stl"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            files.reduce((p, file) => p.then(() => handleFileSelect(file)), Promise.resolve());
          }}
        />
        {isUploading && (
          <div className="w-full mt-4 space-y-2">
            <Progress value={uploadProgress || 0} className="w-full" />
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>{uploadProgress || 0}%</span>
              <span>
                {uploadProgress >= 99
                  ? 'Verarbeite STL-Datei…'
                  : 'Datei wird hochgeladen…'}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-right">
                  {(() => {
                    if (uploadProgress >= 99) {
                      const procEst = Math.max(3, Math.round((uploadFileSize / 1024 / 1024) * 3));
                      const rem = Math.max(0, procEst - (uploadElapsed - Math.max(0, uploadElapsed - procEst)));
                      return rem > 0 ? `Noch ~${procEst}s` : 'Fast fertig…';
                    }
                    if (uploadProgress > 2) {
                      const rem = Math.round(uploadElapsed * (100 - uploadProgress) / uploadProgress);
                      if (rem > 60) return `Noch ~${Math.round(rem / 60)}min`;
                      if (rem > 0) return `Noch ~${rem}s`;
                    }
                    return `${uploadElapsed}s`;
                  })()}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); cancelUpload(); onUploadProgress?.(0); onUploadComplete?.(); }}
                  className="flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 hover:bg-red-100 hover:text-red-700 border border-gray-300 hover:border-red-400 rounded transition-colors"
                  title="Upload abbrechen"
                >
                  <X className="w-3 h-3" />
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
